// Classification of errors that originate inside Stripe.js rather than our code.
//
// Stripe.js rethrows some errors synchronously from its own window `message`
// listener even when our promise `await` already caught them, so they reach
// Bugsnag as *unhandled* no matter what paymentDetails.jsx does. The only lever
// is Bugsnag's `onError`, which is where classifyStripeJsError runs.

const STRIPE_JS_HOST = "https://js.stripe.com/";

export const STRIPE_ELEMENT_NOT_READY_PATTERN =
  /could not retrieve data from the specified Element/i;

// Stripe's `card_error`s ("Your card was declined.") are written for the
// cardholder. Everything else (invalid_request_error, api_error, the Apple Pay
// sheet failures) is written for developers and must never be shown verbatim.
export const PAYMENT_GENERIC_ERROR_MESSAGE =
  "We couldn't process your payment. Please try again or use a different payment method.";

export function patientFacingStripeError(error) {
  if (error && error.type === "card_error" && error.message) {
    return error.message;
  }
  return PAYMENT_GENERIC_ERROR_MESSAGE;
}

// True for the rejection confirmSetup produces when the PaymentElement iframe
// is not mounted or has not emitted `ready` yet.
export function isStripeElementNotReadyError(error) {
  if (!error) return false;
  if (error.name === "IntegrationError") return true;
  return STRIPE_ELEMENT_NOT_READY_PATTERN.test(error.message || "");
}

function topFrameFile(event) {
  const err = event && event.errors && event.errors[0];
  const frame = err && err.stacktrace && err.stacktrace[0];
  return frame && typeof frame.file === "string" ? frame.file : "";
}

// Bugsnag `onError` callback for Stripe.js-originated errors. Returns false to
// drop the event, true to keep it (possibly downgraded).
//
// - "Frame not initialized." is Stripe's internal PaymentRequest iframe being
//   torn down mid-flight (checkout redirect, component unmount). It is dropped:
//   the failures that matter on that path are notified explicitly, as handled
//   warnings with metadata, by paymentDetails.jsx.
// - The IntegrationError for an unready PaymentElement is kept but marked
//   handled/warning: the patient is shown a retry message, and after the ready
//   gate any residual event means the iframe genuinely failed to load, which
//   we want to see.
export function classifyStripeJsError(event) {
  if (!topFrameFile(event).startsWith(STRIPE_JS_HOST)) {
    return true;
  }
  const err = event.errors[0];
  const message = err.errorMessage || "";

  if (message === "Frame not initialized.") {
    return false;
  }

  if (
    err.errorClass === "IntegrationError" &&
    STRIPE_ELEMENT_NOT_READY_PATTERN.test(message)
  ) {
    event.unhandled = false;
    event.severity = "warning";
  }
  return true;
}
